import {
  Button,
  Card,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import React from "react";
import { Link, useNavigate } from "react-router-dom";

import RichTextEditor from "./components/RichTextEditor";

type NewsCategory = {
  id: number;
  name: string;
  slug: string;
};

export default function AdminNewsNew() {
  const nav = useNavigate();
  const [categories, setCategories] = React.useState<NewsCategory[]>([]);
  const [form, setForm] = React.useState({
    slug: "",
    title: "",
    description: "",
    created_by: "",
    cover_image: "",
    published: true,
    category_id: "" as string | "",
  });
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      const res = await fetch("/api/admin/news-categories");
      const data = (await res.json()) as { status: string; data: NewsCategory[] };
      setCategories(data.data || []);
    })();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    const res = await fetch("/api/admin/news", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slug: form.slug,
        title: form.title,
        description: form.description,
        created_by: form.created_by || null,
        cover_image: form.cover_image || null,
        published: form.published,
        category_id: form.category_id ? Number(form.category_id) : null,
      }),
    });
    const ok = res.ok;
    setSaving(false);
    if (ok) {
      setMsg("บันทึกสำเร็จ");
      nav("/admin/news");
    } else {
      setMsg("บันทึกไม่สำเร็จ");
    }
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/admin/upload", { method: "POST", body: fd });
    const data = (await res.json()) as { status: string; url?: string };
    if (data.status === "ok" && data.url) {
      setForm((prev) => ({ ...prev, cover_image: data.url! }));
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl">สร้างข่าวใหม่</h1>
        <Button asChild variant="outline">
          <Link to="/admin/news">กลับไปหน้ารายการ</Link>
        </Button>
      </div>
      <Card className="p-4 bg-card/60">
        <form onSubmit={create} className="grid gap-3">
          <div className="grid gap-1">
            <Label>Slug</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              required
            />
          </div>
          <div className="grid gap-1">
            <Label>ชื่อข่าว</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>
          <div className="grid gap-1">
            <Label>ผู้เขียน</Label>
            <Input
              value={form.created_by}
              onChange={(e) => setForm({ ...form, created_by: e.target.value })}
              placeholder="เช่น ทีมงาน Lucky News"
            />
          </div>
          <div className="grid gap-1">
            <Label>หมวดหมู่</Label>
            <Select
              value={form.category_id}
              onValueChange={(v) => setForm({ ...form, category_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="เลือกหมวดหมู่" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">ไม่ระบุหมวดหมู่</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label>รูปปกข่าว</Label>
            <Input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleUpload} />
            {form.cover_image && (
              <img
                src={form.cover_image}
                alt="cover"
                className="mt-2 h-32 w-full object-cover rounded-md border border-border/60"
              />
            )}
          </div>
          <div className="grid gap-1">
            <Label>เนื้อหาข่าว (คำอธิบายยาว)</Label>
            <RichTextEditor
              value={form.description}
              onChange={(html) => setForm({ ...form, description: html })}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="pub"
              checked={form.published}
              onCheckedChange={(v) => setForm({ ...form, published: Boolean(v) })}
            />
            <Label htmlFor="pub">เผยแพร่</Label>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? "กำลังบันทึก..." : "บันทึกข่าว"}
            </Button>
            <Button type="button" variant="outline" onClick={() => nav("/admin/news")}>
              ยกเลิก
            </Button>
          </div>
        </form>
        {msg && <p className="text-xs mt-3 text-foreground/70">{msg}</p>}
      </Card>
    </div>
  );
}
